import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { IoMdArrowBack } from 'react-icons/io';
import { FiEdit2, FiEye, FiCopy, FiCheck } from 'react-icons/fi';
import { MdClose } from 'react-icons/md';

import { fetchTenant } from '../../redux/tenantActions';
import { fetchVPAs } from '../../redux/vpaActions';
import { fetchProgramWithId } from '../../redux/programActions';
import { useCollectionMode } from '../../hooks/useCollectionMode';
import PlaceholderImage from '../../assests/placeholder.jpg';
import type { VpaRecord } from '../../types/domain';
import { showErrorToast, showSuccessToast } from '../../utils/toast';

type RootState = any;

interface VpaWithMeta extends VpaRecord {
  createdAt?: string;
  qrImage?: string;
}

function TenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isWalletAddressCollection: walletMode } = useCollectionMode();
  const { vpa, loading: vpaLoading } = useSelector((state: RootState) => state.vpa);
  const { tenant_details, loading } = useSelector((state: RootState) => state.tenant);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedQR, setSelectedQR] = useState<string | null>(null);
  const [copyStatus, setCopyStatus] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (!id) return;
    // @ts-expect-error thunk
    dispatch(fetchTenant(id));
    // @ts-expect-error thunk
    dispatch(fetchVPAs(id));
  }, [dispatch, id]);

  useEffect(() => {
    const pid = tenant_details?.programId;
    if (pid != null) {
      // @ts-expect-error thunk
      dispatch(fetchProgramWithId(pid));
    }
  }, [dispatch, tenant_details?.programId]);

  const handleClick = () => {
    try {
      navigate(`/tenant/edit/${id}`);
    } catch (error) {
      showErrorToast('Unable to open edit page');
    }
  };

  const handleVpaClick = (tenantId: number) => {
    try {
      navigate(`/tenant/vpa/${tenantId}`);
    } catch (error) {
      showErrorToast(walletMode ? 'Unable to open wallets page' : 'Unable to open VPA page');
    }
  };

  const handleShowQR = (qrImage: string) => {
    setSelectedQR(qrImage);
    setShowQRModal(true);
  };

  const handleCloseQR = () => {
    setShowQRModal(false);
    setSelectedQR(null);
  };

  const handleCopyVPA = async (vpaValue: string, index: number) => {
    try {
      await navigator.clipboard.writeText(vpaValue);
      showSuccessToast(walletMode ? 'Copied to clipboard' : 'VPA copied to clipboard');
      setCopyStatus((prev) => ({ ...prev, [index]: true }));
      setTimeout(() => {
        setCopyStatus((prev) => ({ ...prev, [index]: false }));
      }, 2000);
    } catch (err) {
      showErrorToast(walletMode ? 'Failed to copy address' : 'Failed to copy VPA');
    }
  };

  const formatDate = (input?: string) => {
    if (!input) return '';
    const date = new Date(input);
    const options: Intl.DateTimeFormatOptions = {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    };
    const formattedDate = date.toLocaleString('en-US', options).replace(',', '');
    return `Added ${formattedDate}`;
  };

  const vpaList: VpaWithMeta[] =
    vpa && vpa[0] && 'vpa' in vpa[0]
      ? (vpa[0].vpa as VpaWithMeta[]).slice().sort(
          (a, b) =>
            new Date(b.createdAt ?? 0).getTime() -
            new Date(a.createdAt ?? 0).getTime(),
        )
      : [];

  return (
    <div className="h-screen bg-white overflow-auto">
      {(loading || vpaLoading) && (
        <div className="fixed inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="w-8 h-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {tenant_details && (
        <div className="h-full flex flex-col">
          <div className="bg-white border-b shadow-sm sticky top-0 z-30">
            <div className="max-w-[1200px] mx-auto">
              <div className="px-4 sm:px-6">
                <div className="flex items-center h-16">
                  <button
                    type="button"
                    onClick={() => navigate(`/program/${tenant_details.programId}`)}
                    className="flex items-center justify-center w-10 h-10 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <IoMdArrowBack className="w-5 h-5 text-gray-600" />
                  </button>
                  <div className="flex-1 flex justify-center">
                    <div className="text-center">
                      <h1 className="text-lg font-semibold text-gray-900">
                        {tenant_details.tenantName}
                      </h1>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleClick}
                    className="flex items-center justify-center w-10 h-10 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <FiEdit2 className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 bg-gray-50/80">
            <div className="max-w-5xl mx-auto p-4 sm:p-6">
              <div className="space-y-6">
                <div className="relative overflow-hidden rounded-xl shadow-soft">
                  <img
                    src={
                      tenant_details.storeImage
                        ? `${tenant_details.storeImage}?t=${new Date().getTime()}`
                        : PlaceholderImage
                    }
                    alt="Store"
                    className="w-full h-56 sm:h-64 object-cover"
                  />
                </div>

                <div className="flex items-center justify-between bg-white p-5 rounded-xl shadow-soft border border-gray-100">
                  <div className="text-gray-900">
                    {tenant_details.vpaCount}{' '}
                    {walletMode ? 'wallets' : 'VPAs'}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleVpaClick(tenant_details.tenantId)}
                    className="px-5 py-2.5 bg-brand text-white rounded-xl hover:bg-brand-dark active:scale-[0.98] transition-all text-sm font-semibold shadow-soft"
                  >
                    {walletMode ? 'Add wallet' : 'Add VPA'}
                  </button>
                </div>

                <div className="bg-white rounded-xl shadow-soft border border-gray-100 p-6">
                  <div className="flex items-center mb-7 px-2 gap-4">
                    <div className="flex-grow">
                      <h2 className="text-xl font-semibold text-gray-900 mb-1">
                        {tenant_details.tenantBrand}
                      </h2>
                    </div>
                    {tenant_details.storeLogo && (
                      <img
                        src={`${tenant_details.storeLogo}?t=${new Date().getTime()}`}
                        alt="Store Logo"
                        className="w-20 h-10 object-fill rounded-md"
                      />
                    )}
                  </div>

                  <div className="space-y-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">
                            Contact Person
                          </label>
                          <div className="text-gray-900">
                            {tenant_details.storeCoordinator}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">
                            Phone Number
                          </label>
                          <div className="text-gray-900">{tenant_details.phone}</div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="space-y-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">
                            Address
                          </label>
                          <div className="text-gray-900">{tenant_details.address}</div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-500 mb-1">
                              Wing
                            </label>
                            <div className="text-gray-900">
                              {tenant_details.wing || '-'}
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-500 mb-1">
                              Floor
                            </label>
                            <div className="text-gray-900">
                              {tenant_details.floor || '-'}
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-500 mb-1">
                              State
                            </label>
                            <div className="text-gray-900">{tenant_details.state}</div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-500 mb-1">
                              City
                            </label>
                            <div className="text-gray-900">{tenant_details.city}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {vpaList.length > 0 && (
                  <div className="bg-white rounded-xl shadow-soft border border-gray-100 p-6 mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      {vpaList.length}{' '}
                      {walletMode ? 'wallets added' : 'VPAs Added'}
                    </h3>
                    <div className="space-y-3">
                      {vpaList.map((item, index) => (
                        <div
                          key={index}
                          className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100/80 transition-colors"
                        >
                          <div className="flex items-center justify-between gap-4">
                            <div className="min-w-0 flex-1">
                              <div className="text-sm text-gray-500">
                                {formatDate(item.createdAt)}
                              </div>
                              <div className="text-gray-900 font-medium mt-1 truncate">
                                {item.vpa}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <div className="relative">
                                <button
                                  type="button"
                                  onClick={() => handleCopyVPA(item.vpa ?? '', index)}
                                  className="p-2 text-gray-700 hover:bg-white rounded-full transition-colors"
                                >
                                  {copyStatus[index] ? (
                                    <FiCheck className="w-5 h-5 text-brand" />
                                  ) : (
                                    <FiCopy className="w-5 h-5" />
                                  )}
                                </button>
                                {copyStatus[index] && (
                                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap">
                                    Copied to clipboard
                                  </div>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => handleShowQR(item.qrImage ?? '')}
                                className="p-2 text-gray-600 hover:bg-white rounded-full transition-colors"
                              >
                                <FiEye className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showQRModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden animate-slide-up">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">QR Code</h3>
              <button
                type="button"
                onClick={handleCloseQR}
                className="flex items-center justify-center w-10 h-10 hover:bg-gray-100 rounded-full transition-colors"
              >
                <MdClose className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="p-6 bg-gray-50/50">
              <img
                src={selectedQR || PlaceholderImage}
                alt="QR Code"
                className="w-full aspect-square object-contain bg-white rounded-xl p-4 shadow-soft"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TenantDetailPage;

